export const EARNINGS_ADS_PER_DAY = 6;
export const EARNINGS_MAX_LEVELS = 18;
export const MONTHLY_REFERRAL_CAP_USD = 7000;
export const OWN_POOL_RATE = 0.05;

export function referralRateForLevel(level) {
  return level <= 13 ? 0.05 : 0.01;
}

export function daysForPeriodTab(periodTab) {
  if (periodTab === 'day') return 1;
  if (periodTab === 'week') return 7;
  return 30;
}

export function capForPeriodTab(periodTab) {
  return (MONTHLY_REFERRAL_CAP_USD * daysForPeriodTab(periodTab)) / 30;
}

export function levelCountsArrayFromRows(levelRows, maxLevels = EARNINGS_MAX_LEVELS) {
  const counts = Array(maxLevels).fill(0);
  for (const row of levelRows || []) {
    const level = Number(row.level ?? row.Level);
    const count = Number(row.memberCount ?? row.MemberCount) || 0;
    if (level >= 1 && level <= maxLevels) counts[level - 1] = count;
  }
  return counts;
}

function buildPriceMap(countryPrices, globalAvg) {
  const map = new Map();
  for (const row of countryPrices || []) {
    const id = row.countryId ?? row.CountryID;
    const gross = row.grossPerAdUsd ?? row.GrossPerAdUSD;
    if (id != null) map.set(Number(id), Number(gross) || 0);
  }
  return { map, globalAvg: Number(globalAvg) || 0 };
}

function priceForCountry(priceMap, countryId, userCountryId, globalAvg) {
  if (countryId != null && priceMap.has(Number(countryId))) {
    return priceMap.get(Number(countryId));
  }
  if (userCountryId != null && priceMap.has(Number(userCountryId))) {
    return priceMap.get(Number(userCountryId));
  }
  return globalAvg;
}

export function computeEarningsEstimate({
  levelCounts,
  levelCountsByCountry,
  countryPrices,
  globalAvgGrossPerAdUsd,
  userCountryId,
  periodTab = 'month',
  useUserCountryForAllLevels = false,
}) {
  const days = daysForPeriodTab(periodTab);
  const adsInPeriod = EARNINGS_ADS_PER_DAY * days;
  const { map: priceMap, globalAvg } = buildPriceMap(countryPrices, globalAvgGrossPerAdUsd);

  const ownGross = priceForCountry(priceMap, userCountryId, userCountryId, globalAvg);
  const ownUsd = adsInPeriod * ownGross * OWN_POOL_RATE;

  const counts = Array.isArray(levelCounts)
    ? levelCounts.slice(0, EARNINGS_MAX_LEVELS).map((n) => Math.max(0, Number(n) || 0))
    : levelCountsArrayFromRows(levelCounts);

  let referralRawUsd = 0;
  const byCountryMap = new Map();
  for (const row of levelCountsByCountry || []) {
    const level = Number(row.level ?? row.Level);
    const countryId = Number(row.countryId ?? row.CountryID);
    const count = Number(row.memberCount ?? row.MemberCount) || 0;
    if (!level || level < 1 || level > EARNINGS_MAX_LEVELS) continue;
    const key = `${level}:${countryId}`;
    byCountryMap.set(key, (byCountryMap.get(key) || 0) + count);
  }

  for (let level = 1; level <= EARNINGS_MAX_LEVELS; level += 1) {
    const rate = referralRateForLevel(level);
    const totalAtLevel = counts[level - 1] || 0;

    if (useUserCountryForAllLevels || byCountryMap.size === 0) {
      const gross = priceForCountry(priceMap, userCountryId, userCountryId, globalAvg);
      referralRawUsd += totalAtLevel * adsInPeriod * gross * rate;
      continue;
    }

    let assigned = 0;
    for (const [key, count] of byCountryMap.entries()) {
      const [lvlStr, countryStr] = key.split(':');
      if (Number(lvlStr) !== level) continue;
      const gross = priceForCountry(priceMap, Number(countryStr), userCountryId, globalAvg);
      referralRawUsd += count * adsInPeriod * gross * rate;
      assigned += count;
    }

    const remainder = Math.max(0, totalAtLevel - assigned);
    if (remainder > 0) {
      const gross = priceForCountry(priceMap, userCountryId, userCountryId, globalAvg);
      referralRawUsd += remainder * adsInPeriod * gross * rate;
    }
  }

  const capUsd = capForPeriodTab(periodTab);
  const referralUsd = Math.min(referralRawUsd, capUsd);

  return {
    periodTab,
    ownUsd,
    referralUsd,
    referralRawUsd,
    referralCapped: referralRawUsd > capUsd,
    referralCapUsd: capUsd,
    totalUsd: ownUsd + referralUsd,
    daysInPeriod: days,
  };
}

export function formatUsd(amount, { minimumFractionDigits = 2, maximumFractionDigits = 2 } = {}) {
  const n = Number(amount);
  if (!Number.isFinite(n)) return '$0.00';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits,
    maximumFractionDigits,
  }).format(n);
}

/** Binary tree capacity: L1=2, L2=4, … L18=262144 */
export function maxMembersForLevel(level) {
  const lvl = Number(level);
  if (!Number.isFinite(lvl) || lvl < 1 || lvl > EARNINGS_MAX_LEVELS) return 0;
  return 2 ** lvl;
}

export function clampLevelCounts(counts, maxLevels = EARNINGS_MAX_LEVELS) {
  const src = Array.isArray(counts) ? counts : [];
  const out = Array(maxLevels).fill(0);
  for (let i = 0; i < maxLevels; i += 1) {
    const n = Math.max(0, Math.floor(Number(src[i]) || 0));
    out[i] = Math.min(n, maxMembersForLevel(i + 1));
  }
  return out;
}

function estimateWithCounts(levelCounts, inputs, periodTab) {
  return computeEarningsEstimate({
    levelCounts,
    levelCountsByCountry: inputs?.levelCountsByCountry,
    countryPrices: inputs?.countryPrices,
    globalAvgGrossPerAdUsd: inputs?.globalAvgGrossPerAdUsd,
    userCountryId: inputs?.user?.countryOfResidenceId,
    periodTab,
    useUserCountryForAllLevels: true,
  });
}

/** Full L1–L18 at user's country ad price, after referral cap — true max for the target box. */
export function maxReachableEarningsUsd(inputs, periodTab = 'month') {
  const fullCounts = Array.from({ length: EARNINGS_MAX_LEVELS }, (_, i) => maxMembersForLevel(i + 1));
  const fullEstimate = estimateWithCounts(fullCounts, inputs, periodTab);
  return Math.max(0, Number(fullEstimate.totalUsd) || 0);
}

/**
 * Top-down fill L1→L18 (each up to 2^level) until estimated totalUsd >= targetUsd.
 * Targets above country-reachable max (full tree + referral cap) are clamped — no pointless deeper fill.
 */
export function fillLevelCountsForTarget({ targetUsd, inputs, periodTab = 'month' }) {
  const target = Math.max(0, Number(targetUsd) || 0);
  const levelCounts = Array(EARNINGS_MAX_LEVELS).fill(0);

  const emptyEstimate = estimateWithCounts(levelCounts, inputs, periodTab);
  const referralCapUsd = capForPeriodTab(periodTab);
  const maxReachableUsd = maxReachableEarningsUsd(inputs, periodTab);
  const cappedByMax = target > maxReachableUsd;
  const fillTarget = cappedByMax ? maxReachableUsd : target;

  if (target <= 0 || emptyEstimate.totalUsd >= fillTarget) {
    return {
      levelCounts,
      estimate: emptyEstimate,
      targetUsd: target,
      effectiveTargetUsd: fillTarget,
      maxReachableUsd,
      referralCapUsd,
      cappedByReferralLimit: cappedByMax,
      reached: true,
      totalPeople: 0,
      shortfallUsd: 0,
      ownMeetsTarget: emptyEstimate.totalUsd >= target,
    };
  }

  let reached = false;
  for (let level = 1; level <= EARNINGS_MAX_LEVELS; level += 1) {
    const max = maxMembersForLevel(level);

    levelCounts[level - 1] = max;
    const withMax = estimateWithCounts(levelCounts, inputs, periodTab);
    if (withMax.totalUsd < fillTarget) {
      // Need this level full; continue to deeper levels
      continue;
    }

    // Minimal count at this level that meets the (capped) fill target
    let lo = 0;
    let hi = max;
    let best = max;
    while (lo <= hi) {
      const mid = Math.floor((lo + hi) / 2);
      levelCounts[level - 1] = mid;
      const est = estimateWithCounts(levelCounts, inputs, periodTab);
      if (est.totalUsd >= fillTarget) {
        best = mid;
        hi = mid - 1;
      } else {
        lo = mid + 1;
      }
    }
    levelCounts[level - 1] = best;
    reached = true;
    break;
  }

  const finalEstimate = estimateWithCounts(levelCounts, inputs, periodTab);
  const totalPeople = levelCounts.reduce((sum, n) => sum + n, 0);
  const reachedFill = reached || finalEstimate.totalUsd >= fillTarget;
  return {
    levelCounts: [...levelCounts],
    estimate: finalEstimate,
    targetUsd: target,
    effectiveTargetUsd: fillTarget,
    maxReachableUsd,
    referralCapUsd,
    cappedByReferralLimit: cappedByMax,
    reached: cappedByMax ? reachedFill : reachedFill && finalEstimate.totalUsd >= target,
    totalPeople,
    shortfallUsd: cappedByMax
      ? Math.max(0, maxReachableUsd - finalEstimate.totalUsd)
      : Math.max(0, target - finalEstimate.totalUsd),
    ownMeetsTarget: false,
  };
}


